import React, { useEffect, useState } from 'react';
import Price, { PriceProps } from '../price/Price';
import { CTAButton, CTAProps } from './CTAButton';
import { styles } from './StickyCallToAction.css';
import { ProductCompareBar } from '@jbhi-fi/components/src/ProductCompare';
import { PerksPromoAlert } from '@jbhi-fi/components/src/PerksPromoAlert/PerksPromoAlert';
import { OnlineProductPrice } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import ReactDOM from 'react-dom';
import { Tracker } from '@jbhi-fi/components/src/Tracking';
import { ProductImageFallback } from '@jbhi-fi/components/src/ProductImageFallback/ProductImageFallback';
import { serviceHelper } from '../../../../config/service-helper';

export type StickyCallToActionProps = {
  buttonType?: string;
  productCartData?: any;
  quantity?: number;
  product?: any;
  gtmProduct?: any;
  price?: number;
  ticket?: number;
  cashback?: boolean;
  deliveryTag?: string;
  sku?: number;
  customerWishlist?: any;
  onlineValue?: any;
  gtmData?: any;
  variantAvailable?: boolean;
  isDigital?: boolean;
  openAvailabilityDrawer?: boolean;
  unavailableItemLabel?: string;
  isLoggedIn: boolean;
  isEarlyAccess: boolean;
  isPerksMember: boolean;
  trackingListName?: string | null;
  productCategoryHierarchy?: Array<string>;
  hasPerksPromotion?: boolean;
  isExclusiveDeal?: boolean;
  pricing: OnlineProductPrice;
  shouldDisplayWasPrice?: boolean;
  showPerksPrice?: boolean;
  isCtaVisible: boolean;
  toolTips?: Array<string>;
  checkAvailabilityAction?: () => void;
  bannerLabel?: string;
};

const AddButton = (props: CTAProps) => {
  return <CTAButton {...props} />;
};

const PriceTag = (props: PriceProps) => {
  return <Price {...props} />;
};

export const StickyCallToAction = ({
  buttonType,
  productCartData,
  product,
  gtmProduct,
  deliveryTag,
  variantAvailable,
  isDigital,
  unavailableItemLabel,
  isLoggedIn,
  isEarlyAccess,
  isPerksMember,
  trackingListName,
  productCategoryHierarchy,
  hasPerksPromotion,
  pricing,
  shouldDisplayWasPrice,
  isExclusiveDeal,
  showPerksPrice,
  isCtaVisible,
  toolTips,
  checkAvailabilityAction,
  bannerLabel,
}: StickyCallToActionProps) => {
  const noValidAction =
    buttonType === 'DoNotDisplay' ||
    (!variantAvailable && buttonType !== 'NotifyMe' && buttonType !== 'CheckAvailability');
  const isMaxWidth = noValidAction || isDigital;
  const isMobile = useBreakpoint('md', 'down');
  const [isStickyCTAVisible, setIsStickyCTAVisible] = useState(false);
  const stickyCartContainer = document.getElementsByClassName('site-header')[0];
  const handleTrackClickCta = (label: string) => {
    Tracker.trackCustomEvent({ event: 'click_cta', link_text: label, location: 'pdp-coupon' });
  };
  useEffect(() => {
    if (!isCtaVisible && !isStickyCTAVisible) {
      setIsStickyCTAVisible(true);
    }
  }, [isCtaVisible]);

  const ctaProps = {
    buttonType,
    productCartData,
    product,
    gtmProduct,
    variantAvailable,
    isDigital,
    trackingListName,
    productCategoryHierarchy,
    checkAvailabilityAction,
  };

  const priceTagProps = {
    deliveryTag,
    buttonType,
    variantAvailable,
    pricing,
    shouldDisplayWasPrice,
    isPerksMember,
    showPerksPrice,
    toolTips,
    bannerLabel,
    tooltipPosition: 'top',
  };
  return isMobile ? (
    <div className={`callToActionWrapperMobile ${styles.callToAction}`}>
      <ProductCompareBar noPortal />
      <div className={styles.callToActionWrapper} id="mobile-cta">
        <div className={styles.actionBar({ isMaxWidth })}>
          <PriceTag className={styles.price} isFloatingCta {...priceTagProps} />
        </div>
        {noValidAction ? (
          <div className={styles.unavailableText}>{unavailableItemLabel}</div>
        ) : (
          <AddButton className={styles.actionBar({ isMaxWidth })} {...ctaProps} />
        )}
      </div>
      {hasPerksPromotion ? (
        <PerksPromoAlert
          isStickyMobile
          isLoggedIn={isLoggedIn}
          isEarlyAccess={isEarlyAccess}
          isExclusiveDeal={isExclusiveDeal}
          isPerksMember={isPerksMember}
          messages={window.Jbhifi?.perksPromoAlerts}
          onLogin={() => handleTrackClickCta('Perks login')}
          b2cBaseUrl={serviceHelper.b2cBaseUrl}
        />
      ) : null}
    </div>
  ) : (
    <>
      {stickyCartContainer
        ? ReactDOM.createPortal(
            <div
              className={styles.desktopWrapper({
                isStickyVisible: !isCtaVisible && isStickyCTAVisible,
                show: isStickyCTAVisible,
              })}
            >
              {!isCtaVisible && (
                <div className={styles.desktopGrid} id="desktop-cta">
                  <div className={styles.desktopSticky}>
                    <div className={styles.desktopImage}>
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.title} />
                      ) : (
                        <ProductImageFallback />
                      )}
                    </div>
                    <p className={styles.desktopTitle}>{product.title}</p>
                    <div className={styles.desktopContent}>
                      <PriceTag isStickyDesktop={true} {...priceTagProps} />
                      {noValidAction ? (
                        <div className={styles.unavailableText}>{unavailableItemLabel}</div>
                      ) : (
                        <AddButton className={styles.desktopCta} {...ctaProps} />
                      )}
                    </div>
                  </div>
                  {hasPerksPromotion ? (
                    <PerksPromoAlert
                      isStickyDesktop
                      isLoggedIn={isLoggedIn}
                      isEarlyAccess={isEarlyAccess}
                      isExclusiveDeal={isExclusiveDeal}
                      isPerksMember={isPerksMember}
                      messages={window.Jbhifi?.perksPromoAlerts}
                      onLogin={() => handleTrackClickCta('Perks login')}
                      b2cBaseUrl={serviceHelper.b2cBaseUrl}
                    />
                  ) : null}
                </div>
              )}
            </div>,

            stickyCartContainer
          )
        : null}
    </>
  );
};
